export type PrettyColor =
    | 'blue'
    | 'green'
    | 'yellow'
    | 'cyan'
    | 'pink'
    | 'red'
    | 'indigo'
    | 'teal'
    | 'orange'
    | 'bluegray'
    | 'purple'
    | 'white'
    | 'black'
    | 'dim';

export type PrettyStyleHelperKeys =
    | 'mono'
    | 'bold'
    | 'italic'
    | 'xxs'
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | 'xxl'
    | 'dim'
    | 'upper'
    | 'spaced';
export type PrettyStyleHelpers = Record<
    PrettyStyleHelperKeys,
    Record<string, string>
> &
    Record<
        'bg' | 'fg',
        (
            color?: PrettyColor,
            brightness?: 'dark' | 'main' | 'light'
        ) => Record<string, string>
    > &
    Record<'pos' | 'neg', (color?: PrettyColor) => Record<string, string>>;

const colors: Record<
    PrettyColor,
    { dark: string; main: string; light: string }
> = {
    blue: { dark: '#125386', main: '#2196F3', light: '#cae6fc' },
    green: { dark: '#2a602c', main: '#4caf50', light: '#b2ddb4' },
    yellow: { dark: '#8a6a19', main: '#FBC02D', light: '#fde4a5' },
    cyan: { dark: '#006775', main: '#00BCD4', light: '#91e2ed' },
    pink: { dark: '#801136', main: '#E91E63', light: '#f69ebc' },
    red: { dark: '#86251e', main: '#f44336', light: '#faaea9' },
    indigo: { dark: '#232d64', main: '#3F51B5', light: '#d1d5ed' },
    teal: { dark: '#00534b', main: '#009688', light: '#91d2cc' },
    orange: { dark: '#874400', main: '#F57C00', light: '#fbc791' },
    bluegray: { dark: '#35454c', main: '#607D8B', light: '#bbc7cd' },
    purple: { dark: '#561561', main: '#9C27B0', light: '#d4a2dd' },
    white: { dark: '#CCC', main: '#FFF', light: '#FFF' },
    black: { dark: '#000', main: '#000', light: '#222' },
    dim: { dark: '#777', main: '#999', light: '#AAA' },
};

export const prettyStyleHelpers: PrettyStyleHelpers = {
    mono: { fontFamily: 'menlo, monaco, monospace' },
    bold: { fontWeight: 'bold' },
    italic: { fontStyle: 'italic' },
    xxs: { fontSize: 'xx-small' },
    xs: { fontSize: 'x-small' },
    sm: { fontSize: 'small' },
    md: { fontSize: 'medium' },
    lg: { fontSize: 'large' },
    xl: { fontSize: 'x-large' },
    xxl: { fontSize: 'xx-large' },
    dim: { color: colors.dim.main },
    upper: { textTransform: 'uppercase' },
    spaced: { marginLeft: '2px', marginRight: '2px' },
    bg: (color = 'indigo', brightness = 'main') => ({
        backgroundColor: colors[color][brightness],
        padding: '2px',
        marginTop: '1px',
        marginBottom: '1px',
    }),
    pos: (color = 'indigo') => ({
        backgroundColor: colors[color]['light'],
        color: colors[color]['dark'],
        padding: '2px',
        marginTop: '1px',
        marginBottom: '1px',
        fontWeight: '500',
    }),
    neg: (color = 'indigo') => ({
        backgroundColor: colors[color]['dark'],
        color: colors[color]['light'],
        padding: '2px',
        marginTop: '1px',
        marginBottom: '1px',
        fontWeight: '500',
    }),
    fg: (color = 'indigo', brightness = 'main') => ({
        color: colors[color][brightness],
    }),
};
